import { appStorage } from './betaStorage'
export type YouthRewardSource = 'task' | 'routine_step' | 'routine_complete' | 'list_item'
export type CompanionColour = 'pink' | 'blue' | 'green'

export interface YouthRewardEvent {
  id: string
  participantId: string
  sourceType: YouthRewardSource
  sourceId: string
  amount: number
  createdAt: string
}

export interface YouthCompanionState {
  name: string
  foodBitesSpent: number
  colour: CompanionColour
}

const LEDGER_KEY = 'schub.youthRewards.v1'
const COMPANION_KEY = 'schub.youthCompanion.v1'

function loadLedger(): YouthRewardEvent[] {
  try {
    const parsed = JSON.parse(appStorage.getItem(LEDGER_KEY) || '[]')
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveLedger(events: YouthRewardEvent[]) {
  appStorage.setItem(LEDGER_KEY, JSON.stringify(events))
  window.dispatchEvent(new Event('schub:youth-rewards'))
}

export function awardFoodBites(participantId: string, sourceType: YouthRewardSource, sourceId: string, amount: number) {
  if (amount <= 0) return false
  const ledger = loadLedger()
  const rewardId = `${participantId}:${sourceType}:${sourceId}`
  if (ledger.some((event) => event.id === rewardId)) return false
  saveLedger([...ledger, { id: rewardId, participantId, sourceType, sourceId, amount, createdAt: new Date().toISOString() }])
  return true
}

export function earnedFoodBites(participantId: string) {
  return loadLedger().filter((event) => event.participantId === participantId).reduce((total, event) => total + event.amount, 0)
}

// Team Rewards use the same positive-completion ledger: every Food Bite Nixi earns
// also gives the child one personal reward point. Spending Nixi's food does not
// remove the child's points.
export function earnedChildPoints(participantId: string) {
  return earnedFoodBites(participantId)
}

function validColour(value: unknown): value is CompanionColour {
  return value === 'pink' || value === 'blue' || value === 'green'
}

export function loadCompanionState(participantId: string): YouthCompanionState {
  try {
    const parsed = JSON.parse(appStorage.getItem(`${COMPANION_KEY}:${participantId}`) || '{}')
    const storedName = typeof parsed.name === 'string' && parsed.name.trim() ? parsed.name.trim() : ''
    return {
      name: !storedName || storedName === 'Pix' ? 'Nixi' : storedName,
      foodBitesSpent: Number(parsed.foodBitesSpent) || 0,
      colour: validColour(parsed.colour) ? parsed.colour : 'pink',
    }
  } catch {
    return { name: 'Nixi', foodBitesSpent: 0, colour: 'pink' }
  }
}

export function saveCompanionState(participantId: string, state: YouthCompanionState) {
  appStorage.setItem(`${COMPANION_KEY}:${participantId}`, JSON.stringify(state))
  window.dispatchEvent(new Event('schub:youth-rewards'))
}

export function availableFoodBites(participantId: string) {
  return Math.max(0, earnedFoodBites(participantId) - loadCompanionState(participantId).foodBitesSpent)
}

export function feedCompanion(participantId: string, cost = 5) {
  if (availableFoodBites(participantId) < cost) return false
  const state = loadCompanionState(participantId)
  saveCompanionState(participantId, { ...state, foodBitesSpent: state.foodBitesSpent + cost })
  return true
}

